// The announcer and the impact bank: the two things that are ASSETS (SPEC §16).
//
// Both are generated offline through `mediagen` — voice lines on the speech
// queue, impacts and fortress cracks on the `sfx` queue — and shipped as static
// files. §16 is unambiguous about why: MOSS throughput is "wildly variable — the
// same 33-character line has taken 14.9 s, 17.7 s, 34 s, 54 s and 78 s on
// consecutive runs, and a cold model load costs ~127 s on top. Nothing in a
// 500 ms-tick game can wait on that."
//
// So this file contains no audio, generates none, and commits none. It is the
// interface the asset pipeline will implement, plus a silent implementation that
// is the correct behaviour today: the bank does not exist, so it does not play,
// and it says so rather than pretending.
//
// The one rule that keeps that honest is already in §16 and is enforced by the
// call sites, not by this file: "Every line is also a text event in the feed.
// Audio is never the only channel for anything." A silent announcer must
// therefore cost the player nothing, and it does.

/** The event lines §16 enumerates. Adding one here is adding one to the bank. */
export type AnnouncerLine =
  | 'territory-lost'
  | 'territory-gained'
  | 'front-collapsing'
  | 'fortress-under-attack'
  | 'fortress-critical'
  | 'fortress-destroyed'
  | 'region-lost'
  | 'new-front'
  | 'budget-replenished'
  | 'season-endgame'
  | 'freeze-imminent';

export interface AnnouncerRequest {
  line: AnnouncerLine;
  /**
   * The country whose name clip is concatenated at playback. §16: "Country names
   * are their own clips, generated per country and concatenated at playback, so
   * 'Commander, ' + 'Poland' + ' has taken the northern front' needs three assets
   * rather than one per country pair."
   */
  country?: number;
}

export interface VoiceBank {
  /** Whether the generated bank is present. False in every build that ships no assets. */
  ready(): boolean;
  /** Plays a line. Returns false when there was nothing to play, which is not an error. */
  say(req: AnnouncerRequest): boolean;
}

/** The bank as it exists today: absent. */
export const SilentVoiceBank: VoiceBank = {
  ready: () => false,
  say: () => false,
};

export interface ImpactRequest {
  /** 0..1, the coalesced weight of the flow that caused it. */
  weight: number;
  kill: boolean;
  siege: boolean;
}

export interface ImpactBank {
  ready(): boolean;
  hit(req: ImpactRequest): boolean;
}

export const SilentImpactBank: ImpactBank = {
  ready: () => false,
  hit: () => false,
};

/**
 * Maps a wire feed-line kind onto an announcer line.
 *
 * Deliberately partial. A kind with no announcer line is a line that is read in
 * the feed and not spoken, which is the right default: §16's bank is 60–120 clips
 * and every event that speaks has to earn its slot against the ones that matter
 * more.
 *
 * The numbers are wire/ephemeral.ts's Line* constants, named rather than imported
 * so this module stays free of the protocol.
 */
export function announcerLineFor(kind: number): AnnouncerLine | null {
  switch (kind) {
    case 1: // LineKill
      return 'territory-lost';
    case 0: // LineCapture
      return 'territory-gained';
    case 2: // LineFortressSiege
      return 'fortress-under-attack';
    case 3: // LineFortressFall
      return 'fortress-destroyed';
    case 4: // LineCountryEliminated
      return 'region-lost';
    case 5: // LineBeachhead
      return 'new-front';
    case 6: // LineSeasonFreeze
      return 'freeze-imminent';
    default:
      return null;
  }
}
